import { delay } from '../utils';
import {
  mockCustomers,
  mockProviders,
  mockCategories,
  mockRequests,
  mockJobs,
  mockPayments,
  mockComplaints,
  mockReviews,
  mockNotifications,
  mockCmsPages,
  mockFaqs,
  mockDashboardStats,
  chartRegistrations,
  chartJobsByStatus,
  chartPayments,
  chartCategories,
} from './data';
import { MOCK_ADMIN } from '../constants';
import type {
  Customer,
  ServiceCategory,
  Complaint,
  AppNotification,
  CmsPage,
  FaqItem,
  AccountStatus,
  VerificationStatus,
  ComplaintStatus,
} from '../types';

let customers = [...mockCustomers];
let providers = [...mockProviders];
let categories = [...mockCategories];
let requests = [...mockRequests];
let payments = [...mockPayments];
let complaints = [...mockComplaints];
let reviews = [...mockReviews];
let notifications = [...mockNotifications];
let cmsPages = [...mockCmsPages];
let faqs = [...mockFaqs];

export const mockApi = {
  login: async (email: string, password: string) => {
    await delay(800);
    if (email === MOCK_ADMIN.email && password === MOCK_ADMIN.password) {
      return {
        token: 'mock-admin-token',
        user: {
          id: MOCK_ADMIN.id,
          name: MOCK_ADMIN.name,
          email: MOCK_ADMIN.email,
          role: 'Admin',
          status: 'Active' as AccountStatus,
        },
      };
    }
    throw new Error('Invalid email or password');
  },

  sendResetLink: async (_email: string) => {
    await delay(700);
    return { success: true };
  },

  resetPassword: async (_password: string) => {
    await delay(700);
    return { success: true };
  },

  getDashboard: async () => {
    await delay(400);
    return {
      stats: mockDashboardStats,
      charts: {
        registrations: chartRegistrations,
        jobsByStatus: chartJobsByStatus,
        payments: chartPayments,
        categories: chartCategories,
      },
      recentUsers: customers.slice(0, 5),
      recentProviders: providers.slice(0, 5),
      recentJobs: mockJobs.slice(0, 5),
      recentPayments: payments.slice(0, 5),
    };
  },

  getCustomers: async () => {
    await delay(350);
    return [...customers];
  },

  getCustomer: async (id: string) => {
    await delay(300);
    const customer = customers.find((c) => c.id === id);
    if (!customer) throw new Error('User not found');
    return customer;
  },

  updateCustomerStatus: async (id: string, status: AccountStatus) => {
    await delay(400);
    customers = customers.map((c) => (c.id === id ? { ...c, status } : c));
    return customers.find((c) => c.id === id)!;
  },

  updateCustomer: async (id: string, patch: Partial<Customer>) => {
    await delay(400);
    customers = customers.map((c) => (c.id === id ? { ...c, ...patch } : c));
    return customers.find((c) => c.id === id)!;
  },

  getProviders: async () => {
    await delay(350);
    return [...providers];
  },

  getProvider: async (id: string) => {
    await delay(300);
    const provider = providers.find((p) => p.id === id);
    if (!provider) throw new Error('Provider not found');
    return provider;
  },

  updateProviderStatus: async (id: string, status: AccountStatus) => {
    await delay(400);
    providers = providers.map((p) => (p.id === id ? { ...p, status } : p));
    return providers.find((p) => p.id === id)!;
  },

  updateDocumentStatus: async (
    providerId: string,
    documentId: string,
    status: VerificationStatus,
    rejectionReason?: string
  ) => {
    await delay(400);
    providers = providers.map((p) => {
      if (p.id !== providerId) return p;
      return {
        ...p,
        documents: p.documents.map((d) =>
          d.id === documentId ? { ...d, status, rejectionReason } : d
        ),
        verificationStatus:
          status === 'Rejected'
            ? 'Rejected'
            : p.documents.every((d) => (d.id === documentId ? status : d.status) === 'Approved')
              ? 'Approved'
              : p.verificationStatus,
      };
    });
    return providers.find((p) => p.id === providerId)!;
  },

  getCategories: async () => {
    await delay(300);
    return [...categories];
  },

  getCategory: async (id: string) => {
    await delay(300);
    const cat = categories.find((c) => c.id === id);
    if (!cat) throw new Error('Category not found');
    return cat;
  },

  saveCategory: async (payload: Partial<ServiceCategory> & { id?: string }) => {
    await delay(400);
    if (payload.id) {
      categories = categories.map((c) => (c.id === payload.id ? { ...c, ...payload } as ServiceCategory : c));
      return categories.find((c) => c.id === payload.id)!;
    }
    const created: ServiceCategory = {
      id: `CAT-${900 + categories.length}`,
      name: payload.name || 'New Category',
      description: payload.description || '',
      icon: (payload.name || 'NC').slice(0, 2).toUpperCase(),
      status: payload.status || 'Active',
      createdAt: new Date().toISOString(),
      providerCount: 0,
      requestCount: 0,
      activeJobs: 0,
      completedJobs: 0,
      subcategories: [],
    };
    categories = [created, ...categories];
    return created;
  },

  getRequests: async () => {
    await delay(350);
    return [...requests];
  },

  getRequest: async (id: string) => {
    await delay(300);
    const item = requests.find((r) => r.id === id);
    if (!item) throw new Error('Request not found');
    return item;
  },

  getJobs: async () => {
    await delay(350);
    return requests.filter((r) => r.status !== 'Pending');
  },

  getJob: async (id: string) => {
    await delay(300);
    const item = requests.find((r) => r.id === id || r.id.replace('REQ', 'JOB') === id);
    if (!item) throw new Error('Job not found');
    return item;
  },

  getPayments: async () => {
    await delay(350);
    return [...payments];
  },

  getPayment: async (id: string) => {
    await delay(300);
    const item = payments.find((p) => p.id === id);
    if (!item) throw new Error('Payment not found');
    return item;
  },

  getComplaints: async () => {
    await delay(350);
    return [...complaints];
  },

  getComplaint: async (id: string) => {
    await delay(300);
    const item = complaints.find((c) => c.id === id);
    if (!item) throw new Error('Complaint not found');
    return item;
  },

  updateComplaint: async (id: string, patch: Partial<Complaint>) => {
    await delay(400);
    complaints = complaints.map((c) => (c.id === id ? { ...c, ...patch } : c));
    return complaints.find((c) => c.id === id)!;
  },

  getReviews: async () => {
    await delay(350);
    return [...reviews];
  },

  getReview: async (id: string) => {
    await delay(300);
    const item = reviews.find((r) => r.id === id);
    if (!item) throw new Error('Review not found');
    return item;
  },

  getNotifications: async () => {
    await delay(350);
    return [...notifications];
  },

  getNotification: async (id: string) => {
    await delay(300);
    const item = notifications.find((n) => n.id === id);
    if (!item) throw new Error('Notification not found');
    return item;
  },

  sendNotification: async (payload: Omit<AppNotification, 'id' | 'createdAt' | 'status' | 'recipients' | 'recipientCount'> & { recipientCount?: number }) => {
    await delay(600);
    const created: AppNotification = {
      id: `NTF-${9000 + notifications.length}`,
      title: payload.title,
      message: payload.message,
      target: payload.target,
      recipientName: payload.recipientName,
      recipientCount: payload.recipientCount || 1,
      createdAt: new Date().toISOString(),
      status: 'Sent',
      recipients: [
        {
          id: `RCP-NEW-0`,
          name: payload.recipientName || 'Audience',
          status: 'Sent',
        },
      ],
    };
    notifications = [created, ...notifications];
    return created;
  },

  getCmsPages: async () => {
    await delay(300);
    return [...cmsPages];
  },

  getCmsPage: async (id: string) => {
    await delay(250);
    const item = cmsPages.find((p) => p.id === id);
    if (!item) throw new Error('Page not found');
    return item;
  },

  saveCmsPage: async (id: string, patch: Partial<CmsPage>) => {
    await delay(400);
    cmsPages = cmsPages.map((p) =>
      p.id === id ? { ...p, ...patch, updatedAt: new Date().toISOString() } : p
    );
    return cmsPages.find((p) => p.id === id)!;
  },

  getFaqs: async () => {
    await delay(300);
    return [...faqs];
  },

  saveFaq: async (payload: Partial<FaqItem> & { id?: string }) => {
    await delay(400);
    if (payload.id) {
      faqs = faqs.map((f) =>
        f.id === payload.id ? { ...f, ...payload, updatedAt: new Date().toISOString() } as FaqItem : f
      );
      return faqs.find((f) => f.id === payload.id)!;
    }
    const created: FaqItem = {
      id: `FAQ-${100 + faqs.length}`,
      question: payload.question || '',
      answer: payload.answer || '',
      status: payload.status || 'Draft',
      updatedAt: new Date().toISOString(),
    };
    faqs = [created, ...faqs];
    return created;
  },

  deleteFaq: async (id: string) => {
    await delay(350);
    faqs = faqs.filter((f) => f.id !== id);
  },

  updateComplaintStatus: async (id: string, status: ComplaintStatus, reason?: string) => {
    await delay(400);
    complaints = complaints.map((c) =>
      c.id === id
        ? {
            ...c,
            status,
            resolutionReason: reason || c.resolutionReason,
            history: [
              ...c.history,
              { id: `CH-${Date.now()}`, label: status, date: new Date().toISOString() },
            ],
          }
        : c
    );
    return complaints.find((c) => c.id === id)!;
  },
};

export type MockApi = typeof mockApi;
